/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { createContext, useContext, type PropsWithChildren } from 'react';
import type { PixelConfig } from '@/lib/tracking/pixel-config.server';

const DEFAULT_PIXEL_CONFIG: PixelConfig = {
    facebook: { enabled: false, pixelId: null },
    tiktok: { enabled: false, pixelId: null },
};

/**
 * The TikTok/Facebook pixel IDs come from SFCC site preferences via the
 * shared tracking-config Custom API (src/lib/tracking/pixel-config.server.ts),
 * fetched once per request in the root loader — same as `oneTrustConfig`/
 * `pinterestConfig` (see src/root.tsx). Unlike those two, this value needs to
 * reach `useAnalytics()` (via `initializeEngagementAdapters` in
 * src/lib/adapters/engagement/register.ts), which runs outside any
 * component's own props. A React context populated once at the root is used
 * instead of a module-level global so the value is guaranteed available
 * during render, before any descendant's effects (which is what triggers
 * adapter initialization) can run.
 */
const PixelConfigContext = createContext<PixelConfig>(DEFAULT_PIXEL_CONFIG);

export function PixelConfigProvider({ value, children }: PropsWithChildren<{ value: PixelConfig }>) {
    return <PixelConfigContext.Provider value={value}>{children}</PixelConfigContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePixelConfig(): PixelConfig {
    return useContext(PixelConfigContext);
}
